import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  TablePagination,
  CircularProgress,
  Alert,
} from '@mui/material';
import { format } from 'date-fns';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/context/AuthContext';

export enum CreditProductType {
  FILE = 'FILE',
  ORDER = 'ORDER',
  MANUAL_ADJUSTMENT = 'MANUAL_ADJUSTMENT',
}

interface CreditTransaction {
  id: string;
  type: string;
  productId?: string | null;
  productType?: string | null;
  productDescription?: string | null;
  productName?: string | null;
  customerCompanyName?: string | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
  creditAmount: number;
  previousBalanceAmount: number;
  createdAt: string;
}

const CreditTransactionPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.userType === 'admin';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) =>
    format(new Date(dateString), 'dd-MM-yyyy HH:mm');

  const formatCurrency = (amount: number) => {
    return amount.toFixed(0);
  };

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getTransactions({
          page: page + 1,
          limit: rowsPerPage,
        });
        setTransactions(response.transactions);
        setTotal(response.total);
      } catch (err: any) {
        console.error('Error loading transactions:', err);
        setError(err.message || t('creditTransaction.errors.failedToLoad'));
        setTransactions([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('creditTransaction.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.description')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.productType')}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Typography variant="h6">
                          {t('creditTransaction.table.customer')}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.type')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.creditChanged')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.previousBalance')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('creditTransaction.table.date')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map(transaction => {
                    const isCredit =
                      transaction.type.toLowerCase() === 'credit';
                    const amountSign = isCredit ? '+' : '-';
                    return (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {transaction.productType ===
                          CreditProductType.MANUAL_ADJUSTMENT
                            ? (transaction.productName ?? '—')
                            : (transaction.productDescription ?? '—')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              transaction.productType
                                ? t(
                                    `creditTransaction.productType.${transaction.productType}`,
                                    {
                                      defaultValue: transaction.productType,
                                    }
                                  )
                                : '—'
                            }
                            size="small"
                            color="secondary"
                            variant="filled"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {transaction.customerCompanyName &&
                            transaction.customerCompanyName !== '---'
                              ? transaction.customerCompanyName
                              : [
                                  transaction.customerFirstName,
                                  transaction.customerLastName,
                                ]
                                  .filter(Boolean)
                                  .join(' ') || '—'}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={transaction.type}
                            size="small"
                            color={isCredit ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {amountSign}
                          {formatCurrency(transaction.creditAmount)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(transaction.previousBalanceAmount)}
                        </TableCell>
                        <TableCell>
                          {formatDate(transaction.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('creditTransaction.noTransactionsFound')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreditTransactionPage;
